import { useRef } from "react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { SaveIndicator } from "./SaveIndicator";
import { useSaveIndicator } from "@/hooks/useSaveIndicator";
import { RichTextEditorInput } from "@/components";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

export const AutosaveFormRichTextInput = <T extends Record<string, unknown>>({
  description,
  editorHeight = "260px",
  fieldName,
  form,
  infoTooltip,
  label,
  onBlur,
}: {
  description?: string;
  editorHeight?: string;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  infoTooltip?: string;
  label?: string;
  onBlur: () => Promise<void> | void;
}) => {
  const { handleChange, status } = useSaveIndicator();
  const hasFocusedRef = useRef(false);

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        return (
          <FormItem className="text-slate-700">
            {label && (
              <FormLabel className="inline-flex w-full items-center justify-between">
                {label}
                <SaveIndicator status={status} />
              </FormLabel>
            )}
            {description && (
              <FormDescription>{description}</FormDescription>
            )}
            <FormControl>
              <RichTextEditorInput
                className="bg-slate-50"
                editorHeight={editorHeight}
                infoHint={infoTooltip}
                mode={{
                  onChange: (value: string) => {
                    field.onChange(value);
                    if (hasFocusedRef.current) {
                      handleChange();
                    }
                  },
                  type: "html",
                  value: field.value as string,
                }}
                onBlur={() => {
                  void onBlur();
                }}
                onFocus={() => {
                  hasFocusedRef.current = true;
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
